import { ChangeEvent, useRef, useState } from 'react';
import {
    Flex,
    Text,
    Button,
    Box,
    Heading,
    useToast,
    Select,
    Icon,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
} from '@chakra-ui/react';

import { useNavigate } from 'react-router';

import PageHeader from 'layout/pageHeader';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { useLazyExportClientProductsQuery, useUploadClientProductsMutation } from 'core/API/productsApi';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import MDropZone from 'components/molecules/MDropZone';
import { linkStyle } from 'shared/styles';
import ARetLangSelector from 'components/atoms/ARetLangSelector';
import { TProductKeywordsUploadParams, TRetailerProp } from 'shared/types';
import icons from 'shared/icons';
import { isFetchBaseQueryError } from 'utils/erros';
import { useTranslation } from 'helpers/languages/translations';
import FileSaver from 'file-saver';
import DescriptionHtml from 'components/molecules/DescriptionHtml';
import { useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import * as productManagement from 'pages/ProductManagement';
import RSelect from 'components/atoms/formInputs/RSelect';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { alphaNumericSort } from 'utils/strings';

const ProductsUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [retailer, setRetailer] = useState<TRetailerProp>('default');
    const [selectedRetailers, setSelectedRetailers] = useState<string[]>([]);
    const [lang, setLang] = useState<string>('default');
    const [uploadMethod, setUploadMethod] = useState<'keep' | 'override'>('keep');
    const [retailerType, setRetailerType] = useState<'single_retailer' | 'multi_retailer'>('single_retailer');
    const [retailerTemplateType, setRetailerTemplateType] = useState<'single_retailer' | 'multi_retailer'>('single_retailer');
    const {
        data: retailersData,
        isLoading: isGetRetailerLoading,
        isError: isGetRetailerError,
    } = useGetRetailerByClientQuery();

    const navigate = useNavigate();
    const translate = useTranslation();
    const [exportProductInfo, { isLoading: isExportLoading }] = useLazyExportClientProductsQuery();
    const toast = useToast();
    const [uploadProducts, { isLoading }] = useUploadClientProductsMutation();
    const modalInitialRef = useRef(null);
    const { isOpen, onOpen, onClose } = useDisclosure();


    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };


    const generateRetailerOptions = () => {
        const sortedRetailers = retailersData && [...retailersData.data];
        sortedRetailers?.sort((a, b) => alphaNumericSort(a.name, b.name));
        return !isGetRetailerLoading &&
            !isGetRetailerError &&
            sortedRetailers
            ? sortedRetailers.map((r) => ({ label: r.name, value: r.name }))
            : []

    }

    const goBack = () => navigate(-1);

    const renderModal = () => {
        const title = translate('product.upload.retailer.selectModal.title');
        const bodyContent =
            (
                <Box>
                    <Text mb={7} color="gray.700" fontStyle={"italic"}>{translate('product.upload.retailer.selectModal.description')}</Text>
                    <Text>{translate('product.upload.retailer.type')}</Text>
                    <Box my={3} mb={5}>
                        <Select
                            bg="white"
                            value={retailerTemplateType}
                            onChange={(e) => {
                                setRetailerTemplateType(e.target.value as 'single_retailer' | 'multi_retailer');
                            }}
                            borderColor="gray.200"
                            borderRadius={6}
                            size="md"
                        >
                            <option value="single_retailer">
                                {translate('product.upload.retailer.type.single_retailer')}
                            </option>
                            <option value="multi_retailer">
                                {translate('product.upload.retailer.type.multi_retailer')}
                            </option>
                        </Select>
                    </Box>
                    {
                        retailerTemplateType === "multi_retailer" && (
                            <>
                                <Text mb={3}>{translate('product.upload.retailer.selectModal.retailers')}</Text>
                                <RSelect
                                    onChange={
                                        setSelectedRetailers as any
                                    }
                                    selectedOptions={selectedRetailers || []}
                                    isMulti
                                    setAll={true}
                                    options={
                                        generateRetailerOptions()
                                    }
                                />

                            </>)

                    }
                </Box>
            )

        return (
            <Modal isCentered initialFocusRef={modalInitialRef} isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1} ref={modalInitialRef}>
                        <Text as="h3">
                            {title}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={isLoading} />
                    <ModalBody fontSize="sm">{bodyContent}</ModalBody>

                    <ModalFooter>
                        <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                            {translate('product.upload.modal.cancel')}
                        </Button>
                        <Button colorScheme="blue" onClick={() => {
                            getTemplate()
                        }}
                            isDisabled={isExportLoading || (retailerTemplateType === 'multi_retailer' && selectedRetailers.length === 0)}
                        >
                            {translate('product.upload.modal.download_template')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const getTemplate = async () => {
        try {
            const exportParams: TProductKeywordsUploadParams = {
                isTemplate: true,
                isTitleSplit: false,
                lang: 'default',
                retailer: 'default',
            }
            if (retailerTemplateType === 'multi_retailer' && selectedRetailers.length > 0) {
                exportParams['isMultiRetailer'] = true;
                exportParams['retailersList'] = selectedRetailers;

            }

            const { data: res, isError } = await exportProductInfo(exportParams)
            if (isError || !res) throw new Error(translate('product.upload.template.fileName') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('product.upload.template.fileName') as string);
            onClose();
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
            onClose();
        }
    };

    const handleUploadProducts = async (_: any, applyCustom = false) => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            bodyFormData.append('isTitleSplit', 'false');
            bodyFormData.append('retailer', retailer === 'default' ? 'default' : `${retailer.id}`);
            bodyFormData.append('lang', lang);
            bodyFormData.append('uploadMethod', uploadMethod);
            bodyFormData.append('applyCustom', `${applyCustom ? 'true' : 'false'}`);
            bodyFormData.append('uploadType', retailerType);

            const res = await uploadProducts(bodyFormData);
            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorType = (errorObj.data as any).type as string | undefined;
                    if (errorType === 'wrong_multiretailer_template') {
                        throw new Error(translate('product.upload.error.wrong_multiretailer_template') as string);
                    }
                    if (errorType === 'empty_file') {
                        throw new Error(translate('product.upload.error.empty_file') as string);
                    }
                    if (errorType === 'wrong_singleretailer_template') {
                        throw new Error(translate('product.upload.error.wrong_singleretailer_template') as string);
                    }
                    const invalidHeaders = (errorObj.data as any).invalid_headers as string[] | undefined;
                    if (invalidHeaders && invalidHeaders.length) {
                        const invalidHeadersStr = invalidHeaders.map((h) => `« ${h} »`).join(', ');
                        throw new Error(translate('product.upload.error.invalidHeaders', { headers: invalidHeadersStr }) as string);
                    }
                    const retailersNotFound = (errorObj.data as any).retailers_not_found as string[] | undefined;
                    if (retailersNotFound && retailersNotFound.length) {
                        const retailersNotFoundStr = retailersNotFound.map((r) => `« ${r} »`).join(', ');
                        throw new Error(translate('product.upload.error.retailersNotFound', { retailers: retailersNotFoundStr }) as string);
                    }

                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('product.upload.error') as string);
            }

            setUploadedFile(null);
            goBack();
            toast({
                title: translate('product.upload.succes'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;
        const handleChangeUploadMethod = (e: ChangeEvent<HTMLSelectElement>) => {
            setUploadMethod(e.target.value as 'keep' | 'override');
        };

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start" mr={5}>
                <Text my={2} >
                    {translate('product.upload.dataDescription', {
                        exemple: <Text as="i">{translate('product.upload.fileExemples')}</Text>,
                    })}
                </Text>
                <Text mb={2}>{translate('product.upload.customNote')}</Text>
                <Text mb={5}>
                    {translate('product.upload.recommendation', {
                        template: (
                            <Text onClick={
                                () => {
                                    setRetailerTemplateType(retailerType)
                                    setSelectedRetailers([]);
                                    onOpen()
                                }
                            } as="span" {...linkStyle}>
                                {translate('product.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>
                <Text>{translate('product.upload.retailer.type')}</Text>
                <Box my={3} mt={2}>
                    <Select
                        bg="white"
                        value={retailerType}
                        onChange={(e) => {
                            setRetailerType(e.target.value as 'single_retailer' | 'multi_retailer');
                        }}
                        borderColor="gray.200"
                        borderRadius={6}
                        size="md"
                    >
                        <option value="single_retailer">
                            {translate('product.upload.retailer.type.single_retailer')}
                        </option>
                        <option value="multi_retailer">
                            {translate('product.upload.retailer.type.multi_retailer')}
                        </option>
                    </Select>
                </Box>

                <Box my={2}>
                    {retailerType === 'single_retailer' && (
                        <>
                            <Text>
                                {translate(
                                    'product.upload.retailerSelect'
                                )}
                            </Text>
                            <ARetLangSelector
                                selectedRetailer={retailer}
                                selectedLang={lang}
                                setSelectedLang={setLang}
                                setSelectedRetailer={setRetailer}
                                omitLang={true}
                                selectProps={{ size: 'md', mt: 3, mb: 2 }}
                            />
                        </>
                    )}
                </Box>

                <Text>{translate('product.upload.method')}</Text>
                <Select
                    borderRadius={6}
                    size="md"
                    mb={8}
                    mt={2}
                    bg="white"
                    value={uploadMethod}
                    onChange={handleChangeUploadMethod}
                >
                    <option value="keep">{translate('product.upload.method.keep')}</option>
                    <option value="override">{translate('product.upload.method.override')} </option>
                </Select>

                <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        onClick={intentionalyFloatingPromiseReturn(handleUploadProducts)}
                        isLoading={isLoading}
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('product.upload.apply')}
                    </Button>
                </Flex>
            </Flex >
        );
    };

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(21);
    if (readonly) {
        navigate(`/${productManagement.PATH}`);
        return null;
    }

    return (
        <Box>
            <PageHeader
                pageTitle={translate('product.upload.headerInfo') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            {renderModal()}
            <Box px="32px">
                <Flex>
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb={5}>
                        {translate('product.upload.uploadFile')}
                    </Heading>
                </Flex>
                <Flex direction="row">
                    {renderDescriptionSection()}
                    <DescriptionHtml h="fit-content" tradKey="product.upload.description" />
                </Flex>
            </Box>
        </Box>
    );
};

export default ProductsUpload;
